import type { OutreachParsed, OutreachQuote } from '@discovr/types';
import { fmtDeliverableType, UNSPECIFIED_DELIVERABLE } from '@discovr/types';
import { Button } from '../../../components/ui/button';
import { formatMoneyExact } from '../../../lib/format';
import { roundSuffix } from './roundLabel';
import { cn } from '../../../lib/utils';

export function fmtRate(amount: number, currency: string): string {
  const code = currency?.toUpperCase() || 'INR';
  if (code === 'INR' || !Number.isFinite(amount)) return formatMoneyExact(amount);
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  } catch {
    return `${code} ${amount.toFixed(2)}`;
  }
}

export interface QuoteBlockProps {
  parsed?: OutreachParsed;
  quote?: OutreachQuote;
  canWrite: boolean;
  /** Opens the attach dialog for the amounts the assistant could not place on one deliverable — a correction to this reading, so it stays on the card. */
  onAttachUnmatched?: () => void;
}

/** UX 4.3: "the assistant" everywhere in the tab — never "LLM". */
const SOURCE_LABELS: Record<string, string> = { llm: 'the assistant', regex: 'regex', manual: 'you' };
const sameCurrency = (a: string, b: string) => (a?.toUpperCase() || 'INR') === (b?.toUpperCase() || 'INR');
const rateLabel = (deliverable: string) => (deliverable === UNSPECIFIED_DELIVERABLE ? 'deliverable not stated' : fmtDeliverableType(deliverable));

// Go also stores each unmatched amount on the quote as an unattached rate; reading the parse alone keeps them from being listed twice.
function quoteRates(parsed?: OutreachParsed, quote?: OutreachQuote) {
  const unmatched = parsed?.unmatched ?? [];
  return parsed && (parsed.rates.length > 0 || unmatched.length > 0) ? parsed.rates : (quote?.rates ?? []);
}

/** Whether QuoteBlock renders at all. A reply on a closed thread has a bundle but no quote row, so the bundle alone keeps the block. */
export function hasQuoteContent(parsed?: OutreachParsed, quote?: OutreachQuote): boolean {
  return quoteRates(parsed, quote).length > 0 || (parsed?.unmatched ?? []).length > 0 || !!parsed?.bundleDetail;
}

/** The rates read off one creator reply, as information only: the rate actions live in the composer's pill row. */
export function QuoteBlock({ parsed, quote, canWrite, onAttachUnmatched }: QuoteBlockProps) {
  if (!hasQuoteContent(parsed, quote)) return null;
  const unmatched = parsed?.unmatched ?? [];
  const rates = quoteRates(parsed, quote);
  const bundle = parsed?.bundleDetail;
  const listed = bundle ? rates.filter((r) => !(r.deliverable === UNSPECIFIED_DELIVERABLE && r.amount === bundle.amount && sameCurrency(r.currency, bundle.currency))) : rates;
  const rawSource = parsed?.source ?? quote?.rates[0]?.source ?? 'regex';
  const source = SOURCE_LABELS[rawSource] ?? rawSource;
  // UX 4.1: the round count lives on the quote, not only in Task 39's toast.
  const roundLabel = roundSuffix(quote);
  return (
    <div
      data-testid="quote-block"
      className={cn('mt-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-3', !canWrite && 'opacity-90')}
    >
      {listed.length > 0 && <ul className="space-y-1">
        {listed.map((rate, index) => (
          <li key={index} className="text-body-sm-regular tabular-nums text-foreground">
            {fmtRate(rate.amount, rate.currency)} / {rateLabel(rate.deliverable)}
            {'notPitched' in rate && rate.notPitched ? (
              <span aria-label="Not part of what was pitched" className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] text-amber-800">Not pitched</span>
            ) : null}
          </li>
        ))}
      </ul>}
      {unmatched.length > 0 && (
        <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2">
          <p className="text-[12px] font-medium text-amber-900">Not matched to a deliverable</p>
          <ul className="mt-1 space-y-1">
            {unmatched.map((u, index) => (
              <li key={index} className="text-body-sm-regular tabular-nums text-foreground">
                {fmtRate(u.amount, u.currency)}
                {u.quote ? <span title={u.quote} className="ml-2 inline-block max-w-[16rem] truncate align-bottom text-[12px] text-muted-foreground">{u.quote}</span> : null}
              </li>
            ))}
          </ul>
          {canWrite && onAttachUnmatched ? (
            <Button size="sm" variant="outline" className="mt-2" onClick={onAttachUnmatched}>
              Attach to a deliverable
            </Button>
          ) : null}
        </div>
      )}
      {parsed?.bundleDetail ? (
        <p className="mt-1 text-[12px] text-muted-foreground">
          Bundle: {fmtRate(parsed.bundleDetail.amount, parsed.bundleDetail.currency)} for {parsed.bundleDetail.deliverableIds.map(fmtDeliverableType).join(' + ')}
        </p>
      ) : parsed?.bundle ? (
        <p className="mt-1 text-[12px] text-muted-foreground">Bundle: {parsed.bundle}</p>
      ) : null}
      {parsed?.notes ? <p className="mt-1 text-[12px] text-muted-foreground">{parsed.notes}</p> : null}
      <p className="mt-2 text-[11px] text-muted-foreground">{`parsed by ${source}${roundLabel}`}</p>
    </div>
  );
}
